import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay, isYesterday, isSameMonth, isSameYear } from "date-fns";
import { es } from "date-fns/locale";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { 
	Image as ImageIcon,
	VideoLibrary as VideoIcon,
	AttachFile as FileIcon,
	Audiotrack as AudioIcon, 
	Person as PersonIcon, 
	LocationOn as LocationIcon,
	Phone as PhoneIcon,
	CheckCircle as CheckCircleIcon,
	AccessTime,
	Done,
	DoneAll,
	EmojiEmotions as StickerIcon
} from "@material-ui/icons";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import WhatsAppAvatar from "../WhatsAppAvatar";
import { formatContactName } from "../../utils/formatPhoneNumber";
import { getOrderStatusChipLabel } from "../../utils/orderStatusLabels";

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
	},

	pendingTicket: {
		// Removed cursor: "unset" to allow clicking on pending tickets
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	contactNameWrapper: {
		display: "flex",
		justifyContent: "space-between",
	},

	lastMessageTime: {
		justifySelf: "flex-end",
		minWidth: 80,
		textAlign: "right",
	},

	closedBadge: {
		alignSelf: "center",
		justifySelf: "flex-end",
		marginRight: 32,
		marginLeft: "auto",
	},

	contactLastMessage: {
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		minWidth: 0,
		flex: 1,
		display: "block",
	},
	messagePreviewRow: {
		display: "flex",
		alignItems: "center",
		minWidth: 0,
		width: "100%",
		paddingRight: 90,
	},
	listItemTextSecondary: {
		minWidth: 0,
		overflow: "hidden",
		width: "100%",
	},

	lastMessageAckIcon: {
		fontSize: 16,
		verticalAlign: "middle",
		marginRight: 4,
		color: "rgba(0, 0, 0, 0.54)",
	},

	lastMessageAckDoneAllIcon: {
		fontSize: 16,
		verticalAlign: "middle",
		marginRight: 4,
		color: green[500],
	},

	newMessagesCount: {
		alignSelf: "center",
		marginRight: 8,
		marginLeft: "auto",
	},

	badgeStyle: {
		color: "white",
		backgroundColor: green[500],
	},

	acceptButton: {
		minWidth: 36,
		width: 36,
		height: 36,
		padding: 0,
		marginRight: 8,
	},
	acceptButtonContainer: {
		display: "flex",
		alignItems: "center",
		marginRight: 8,
	},

	ticketQueueColor: {
		flex: "none",
		width: "8px",
		height: "100%",
		position: "absolute",
		top: "0%",
		left: "0%",
	},

	userTag: {
		position: "absolute",
		marginRight: 5,
		right: 5,
		bottom: 5,
		background: "#2576D2",
		color: "#ffffff",
		border: "1px solid #CCC",
		padding: 1,
		paddingLeft: 5,
		paddingRight: 5,
		borderRadius: 10,
		fontSize: "0.9em"
	},
	orderLabel: {
		fontSize: "0.65rem",
		marginLeft: 4,
		padding: "1px 4px",
		borderRadius: 4,
		backgroundColor: "#2196F3",
		color: "#fff",
		whiteSpace: "nowrap"
	},
	orderLabelFinalized: {
		backgroundColor: "#4CAF50"
	},
}));

const TicketListItem = ({ ticket, orderStatus, onTicketAccepted, onOpenTicket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const { ticketId } = useParams();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const formatTicketTime = (dateString) => {
		if (!dateString) return "";
		
		const date = parseISO(dateString);
		const now = new Date();
		
		// Today: show time in 12-hour format with a.m./p.m. (with periods)
		if (isSameDay(date, now)) {
			const timeStr = format(date, "h:mm a", { locale: es });
			// Add periods to am/pm -> a.m./p.m.
			return timeStr.replace(/\s(am|pm)/i, (match, period) => {
				return ` ${period.toLowerCase() === 'am' ? 'a.m.' : 'p.m.'}`;
			});
		}
		
		// Yesterday: show "ayer"
		if (isYesterday(date)) {
			return "ayer";
		}
		
		// Same month: show "lun. 25" (day abbreviation and day number)
		if (isSameMonth(date, now)) {
			return format(date, "EEE d", { locale: es });
		}
		
		// Same year: show "Agosto 24" (month name and day)
		if (isSameYear(date, now)) {
			return format(date, "MMMM d", { locale: es });
		}
		
		// Different year: show "Agosto 25, 2025" (month name, day, and year)
		return format(date, "MMMM d, yyyy", { locale: es });
	};

	const formatLastMessage = (message) => {
		if (!message) return "";
		
		// Check for specific message types
		if (message === "Tarjeta de contacto") {
			return (
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
					<PersonIcon style={{ fontSize: '16px' }} />
					Tarjeta de contacto
				</span>
			);
		}
		
		if (message === "Localization" || message.startsWith("Localization -") || message.startsWith("Ubicación -")) {
			return (
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
					<LocationIcon style={{ fontSize: '16px' }} />
					Ubicación
				</span>
			);
		}
		
		// Stickers (webp) – show "Sticker" instead of "Imagen"
		if (message.match(/\.webp$/i)) {
			return (
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
					<StickerIcon style={{ fontSize: '16px' }} />
					Sticker
				</span>
			);
		}
		
		if (message === "Audio") {
			return (
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
					<AudioIcon style={{ fontSize: '16px' }} />
					Audio
				</span>
			);
		}
		
		// Check for other image files
		if (message.match(/\.(jpg|jpeg|png|gif|bmp)$/i)) {
			return (
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
					<ImageIcon style={{ fontSize: '16px' }} />
					Imagen
				</span>
			);
		}
		
		// Check for video files
		if (message.match(/\.(mp4|avi|mov|wmv|flv|webm|mkv)$/i)) {
			return (
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
					<VideoIcon style={{ fontSize: '16px' }} />
					Vídeo
				</span>
			);
		}
		
		// Check for other files
		if (message.startsWith("file.") || message.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|zip|rar|7z)$/i)) {
			return (
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
					<FileIcon style={{ fontSize: '16px' }} />
					Archivo
				</span>
			);
		}
		
		// Check for call messages
		if (message === "Llamada entrante" || message === "Llamada perdida") {
			return (
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
					<PhoneIcon style={{ fontSize: '16px', color: '#ff9800' }} />
					{message}
				</span>
			);
		}
		
		if (message === "Llamada contestada" || message === "Llamada aceptada") {
			return (
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
					<PhoneIcon style={{ fontSize: '16px', color: '#4caf50' }} />
					{message}
				</span>
			);
		}
		
		if (message === "Llamada rechazada") {
			return (
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
					<PhoneIcon style={{ fontSize: '16px', color: '#f44336' }} />
					{message}
				</span>
			);
		}
		
		// For normal text, use MarkdownWrapper to handle bold formatting
		return <MarkdownWrapper>{message}</MarkdownWrapper>;
	};

	// Same tick icons as in chat (MessagesList renderMessageAck)
	const renderLastMessageAck = (ack) => {
		const ackNum = Number(ack);
		if (ackNum === 0) {
			return <AccessTime fontSize="small" className={classes.lastMessageAckIcon} />;
		}
		if (ackNum === 1) {
			return <Done fontSize="small" className={classes.lastMessageAckIcon} />;
		}
		if (ackNum === 2) {
			return <DoneAll fontSize="small" className={classes.lastMessageAckIcon} />;
		}
		if (ackNum === 3 || ackNum === 4) {
			return <DoneAll fontSize="small" className={classes.lastMessageAckDoneAllIcon} />;
		}
		return null;
	};

	const handleAcepptTicket = async id => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
			});
			if (onTicketAccepted) onTicketAccepted();
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
		if (isMounted.current) {
			setLoading(false);
		}
		history.push(`/tickets/${id}`);
	};

	const handleSelectTicket = id => {
		if (typeof onOpenTicket === "function") {
			onOpenTicket(id);
		}
		history.push(`/tickets/${id}`);
	};

	return (
		<React.Fragment key={ticket.id}>
			<ListItem
				dense
				button
				onClick={e => {
					handleSelectTicket(ticket.id);
				}}
				selected={ticketId && +ticketId === ticket.id}
				className={clsx(classes.ticket, {
					[classes.pendingTicket]: ticket.status === "pending",
				})}
			>
				<Tooltip
					arrow
					placement="right"
					title={ticket.queue?.name || "Sem fila"}
				>
					<span
						style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
						className={classes.ticketQueueColor}
					></span>
				</Tooltip>
				<ListItemAvatar>
					<WhatsAppAvatar 
						src={ticket?.contact?.profilePicUrl} 
						name={ticket?.contact?.name || ticket?.contact?.number}
						size={40}
					/>
				</ListItemAvatar>
				<ListItemText
					disableTypography
					classes={{ secondary: classes.listItemTextSecondary }}
					primary={
						<span className={classes.contactNameWrapper}>
							<Typography
								noWrap
								component="span"
								variant="body2"
								color="textPrimary"
								style={{ flex: 1, minWidth: 0, display: "inline-flex", alignItems: "center", gap: 6 }}
							>
								<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									{formatContactName(ticket.contact?.name || ticket.contact?.number || "")}
								</span>
								{ticket.isGroup && (
									<span
										style={{
											fontSize: "0.75rem",
											backgroundColor: "#e0f2f1",
											color: "#00796b",
											borderRadius: 8,
											padding: "2px 6px",
											whiteSpace: "nowrap"
										}}
									>
										Grupo
									</span>
								)}
								{orderStatus?.label && (
									<span
										className={clsx(classes.orderLabel, {
											[classes.orderLabelFinalized]: orderStatus.status === "finalized"
										})}
									>
										{getOrderStatusChipLabel(orderStatus)}
									</span>
								)}
							</Typography>
							{ticket.status === "closed" && (
								<Badge
									className={classes.closedBadge}
									badgeContent={"closed"}
									color="primary"
								/>
							)}
							<div style={{ display: "flex", alignItems: "center", marginRight: 8 }}>
								{ticket.status === "pending" ? (
									<ButtonWithSpinner
										color="primary"
										variant="contained"
										className={classes.acceptButton}
										size="small"
										loading={loading}
										onClick={e => {
											e.stopPropagation();
											handleAcepptTicket(ticket.id);
										}}
										title={i18n.t("ticketsList.buttons.accept")}
									>
										<CheckCircleIcon />
									</ButtonWithSpinner>
								) : (
									<div style={{ width: 36, height: 36 }} />
								)}
							</div>
							{ticket.lastMessage && (
								<Typography
									className={classes.lastMessageTime}
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{formatTicketTime(ticket.updatedAt)}
								</Typography>
							)}
							{ticket.whatsappId && (
								<div className={classes.userTag} title={i18n.t("ticketsList.connectionTitle")}>{ticket.whatsapp?.name}</div>
							)}
						</span>
					}
					secondary={
						<span className={classes.messagePreviewRow}>
							<Typography
								className={classes.contactLastMessage}
								noWrap
								component="span"
								variant="body2"
								color="textSecondary"
							>
								{ticket.lastMessage ? (
									<span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "nowrap", minWidth: 0, overflow: "hidden" }}>
										{!!ticket.lastMessageFromMe && renderLastMessageAck(ticket.lastMessageAck)}
										<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{formatLastMessage(ticket.lastMessage)}</span>
									</span>
								) : (
									<br />
								)}
							</Typography>
							{(() => {
								const openCount =
									(ticket.internalUnread ?? ticket.unreadMessages ?? 0);
								const otherCount = ticket.unreadMessages ?? 0;
								const count = ticket.status === "open" ? openCount : otherCount;

								if (count <= 0) return null;

								return (
									<Badge
										className={classes.newMessagesCount}
										badgeContent={count}
										classes={{
											badge: classes.badgeStyle,
										}}
									/>
								);
							})()}
						</span>
					}
				/>
			</ListItem>
			<Divider variant="inset" component="li" />
		</React.Fragment>
	);
};

export default TicketListItem;
